import {
  Condition,
  ConditionBlock,
  Doc,
  Policy,
  RuleModel,
  SharedDriveModel,
} from '../../../../types';
import {
  RulesEngineForm,
  RulesEngineFormValues,
} from './rules-engine-form-page';
import {
  employeeInfoFields,
  profileFieldOptions,
} from '../../../shared/shared-profile-field-filter';
import { isFeatureEnabled } from '../../../utils/feature-flags';

function migrateLegacyProfileFilter(
  selectedRule: RuleModel,
  conditionBlocks: ConditionBlock[]
) {
  const hasLegacyProfileFilter =
    !!selectedRule.profileFieldFilter?.profileFieldType;
  if (!hasLegacyProfileFilter || !selectedRule.profileFieldFilter) {
    return conditionBlocks;
  }

  const {
    profileFieldType,
    profileFieldValue,
    profileFieldValues,
    profileFieldValueExact,
    profileFieldRegex,
  } = selectedRule.profileFieldFilter;

  const values: string[] = profileFieldValues?.length
    ? profileFieldValues
        .map((v: any) => (typeof v === 'string' ? v : v?.value))
        .filter(Boolean)
    : profileFieldValue
    ? [profileFieldValue]
    : profileFieldRegex
    ? [profileFieldRegex]
    : [];

  if (values.length > 0) {
    const newConditions: Partial<Condition>[] = values.map((val, index) => {
      const isCustomAttribute = profileFieldType?.includes('.');
      let fieldName = profileFieldType;
      let customSchemaName: string | undefined;

      if (isCustomAttribute && profileFieldType) {
        const parts = profileFieldType.split('.');
        if (!profileFieldType.startsWith('customAttribute.')) {
          fieldName = `customAttribute.${profileFieldType}`;
          customSchemaName = parts[0];
        } else {
          // It's already in the correct format, e.g., "customAttribute.Schema.Field"
          customSchemaName = parts.length > 1 ? parts[1] : undefined;
        }
      }

      return {
        id: `migrated-profile-${selectedRule.id}-${index}`,
        fieldInfoType: isCustomAttribute
          ? 'customAttributes'
          : employeeInfoFields.some((f) => f.value === profileFieldType)
          ? 'employeeInformation'
          : 'primaryOrganization',
        customSchemaName: customSchemaName,
        fieldName: fieldName,
        operator: profileFieldValueExact ? 'equals' : 'contains',
        value: val,
        logicalOperator: index < values.length - 1 ? 'OR' : undefined,
        ...(isCustomAttribute && {
          customSchema: customSchemaName
            ? { value: customSchemaName, label: customSchemaName }
            : undefined,
        }),
      };
    });

    if (conditionBlocks.length > 0) {
      conditionBlocks[0].conditions.push(...(newConditions as Condition[]));
    } else {
      conditionBlocks.push({
        id: `migrated-block-profile-${selectedRule.id}`,
        conditions: newConditions as Condition[],
      });
    }
  }

  return conditionBlocks;
}

function migrateLegacyGroupFilter(
  selectedRule: RuleModel,
  conditionBlocks: ConditionBlock[]
) {
  const hasLegacyGroupFilter =
    selectedRule.groupFilter?.hasGroupNameFilter ||
    selectedRule.groupFilter?.hasMemberCountFilter;

  if (!hasLegacyGroupFilter || !selectedRule.groupFilter) {
    return conditionBlocks;
  }

  const {
    hasGroupNameFilter,
    groupNameMode,
    groupName,
    groupNameExact,
    groupNameRegex,
    hasMemberCountFilter,
    memberCountMode,
    memberCountOperator,
    memberCount,
    memberCountRangeLessThan,
    memberCountRangeGreaterThan,
  } = selectedRule.groupFilter;

  const newConditions: Partial<Condition>[] = [];

  if (hasGroupNameFilter) {
    newConditions.push({
      id: `migrated-groupName-${selectedRule.id}`,
      fieldInfoType: 'groupName',
      fieldName: 'groupName',
      operator:
        groupNameMode === 'regex' && groupNameRegex != null
          ? 'contains'
          : groupNameExact
          ? 'equals'
          : 'contains',
      value: groupNameMode === 'regex' ? groupNameRegex : groupName,
    });

    if (hasMemberCountFilter) {
      const lastCond = newConditions[newConditions.length - 1];
      lastCond.logicalOperator = 'OR';
    }
  }

  if (hasMemberCountFilter) {
    newConditions.push({
      id: `migrated-memberCount-${selectedRule.id}`,
      fieldInfoType: 'memberCount',
      fieldName: 'memberCount',
      operator:
        memberCountMode === 'range'
          ? 'between'
          : (memberCountOperator as Condition['operator']),
      value: String(
        memberCountMode === 'range'
          ? memberCountRangeLessThan ?? 0
          : memberCount ?? 0
      ),
      secondaryValue:
        memberCountMode === 'range'
          ? String(memberCountRangeGreaterThan ?? 0)
          : undefined,
    });
  }

  if (newConditions.length > 0) {
    if (conditionBlocks.length > 0) {
      conditionBlocks[0].conditions.push(...(newConditions as Condition[]));
    } else {
      conditionBlocks.push({
        id: `migrated-block-${selectedRule.id}`,
        conditions: newConditions as Condition[],
      });
    }
  }

  return conditionBlocks;
}

function migrateLegacyDriveFilter(
  selectedRule: RuleModel,
  conditionBlocks: ConditionBlock[]
) {
  const { triggerInfo } = selectedRule;
  if (!triggerInfo) {
    return conditionBlocks;
  }

  const hasLegacyDriveFilter =
    (triggerInfo.fileMimeTypes && triggerInfo.fileMimeTypes.length > 0) ||
    (triggerInfo.driveFileSizeFloor ?? 0) > 0 ||
    (triggerInfo.driveFileSizeCeiling ?? 0) > 0 ||
    triggerInfo.fileIsSharedPublic ||
    triggerInfo.fileIsSharedToDomain ||
    triggerInfo.fileSharedToUserExternalToDomain ||
    triggerInfo.fileSharedToUserInternalToDomain ||
    (triggerInfo.fileSharedToSpecificUser?.length ?? 0) > 0 ||
    // Also check for domain-specific sharing options
    triggerInfo.canFindInSearchResults ||
    triggerInfo.mustHaveLinkToAccess;

  if (!hasLegacyDriveFilter) {
    return conditionBlocks;
  }

  const allSharingConditions: Partial<Condition>[] = [];

  const newConditions: Partial<Condition>[] = [];

  if (triggerInfo.fileMimeTypes?.length) {
    triggerInfo.fileMimeTypes.forEach((mimeType, index) => {
      newConditions.push({
        id: `migrated-fileType-${selectedRule.id}-${index}`,
        fieldInfoType: 'fileType',
        fieldName: 'fileType',
        operator: 'equals',
        value: mimeType,
        logicalOperator:
          index < triggerInfo.fileMimeTypes!.length - 1 ? 'OR' : undefined,
      });
    });
  }

  if (newConditions.length > 0) {
    const lastCond = newConditions[newConditions.length - 1];
    lastCond.logicalOperator = 'OR';
  }

  if (
    (triggerInfo.driveFileSizeFloor ?? 0) > 0 ||
    (triggerInfo.driveFileSizeCeiling ?? 0) > 0
  ) {
    const floor = triggerInfo.driveFileSizeFloor ?? 0;
    const ceiling = triggerInfo.driveFileSizeCeiling ?? 0;
    const operator =
      floor > 0 && ceiling > 0
        ? 'between'
        : floor > 0
        ? 'greaterThan'
        : 'lessThan';
    newConditions.push({
      id: `migrated-fileSize-${selectedRule.id}`,
      fieldInfoType: 'fileSize',
      fieldName: 'fileSize',
      operator: operator,
      value: String(operator === 'lessThan' ? ceiling : floor),
      secondaryValue: operator === 'between' ? String(ceiling) : undefined,
      unit: 'Byte',
    });
  }

  if (newConditions.length > 0) {
    const lastCond = newConditions[newConditions.length - 1];
    lastCond.logicalOperator = 'OR';
  }

  const sharingFields = [
    'fileIsSharedPublic',
    'fileIsSharedToDomain',
    'fileSharedToUserExternalToDomain',
    'fileSharedToUserInternalToDomain',
  ];

  for (const field of sharingFields) {
    if ((triggerInfo as any)[field]) {
      let value = 'true';
      if (field === 'fileIsSharedToDomain') {
        if (triggerInfo.canFindInSearchResults) {
          value = 'canFindInSearchResults';
        } else if (triggerInfo.mustHaveLinkToAccess) {
          value = 'mustHaveLinkToAccess';
        }
      }

      allSharingConditions.push({
        id: `migrated-sharing-${field}-${selectedRule.id}`,
        fieldInfoType: 'fileSharing',
        fieldName: field,
        operator: 'equals',
        value: value,
      });
    }
  }

  if (
    triggerInfo.fileSharedToSpecificUser &&
    triggerInfo.fileSharedToSpecificUser.length > 0
  ) {
    triggerInfo.fileSharedToSpecificUser.forEach((user, index) => {
      allSharingConditions.push({
        id: `migrated-sharing-specificUser-${selectedRule.id}-${index}`,
        fieldInfoType: 'fileSharing',
        fieldName: 'fileSharedToSpecificUser',
        operator: 'equals',
        value: user,
        logicalOperator:
          index < triggerInfo.fileSharedToSpecificUser!.length - 1
            ? 'OR'
            : undefined,
      });
    });
  }

  // Join all sharing conditions with OR
  if (allSharingConditions.length > 0) {
    allSharingConditions.forEach((cond, index) => {
      if (index < allSharingConditions.length - 1) {
        cond.logicalOperator = 'OR';
      }
    });
    newConditions.push(...allSharingConditions);
  }

  if (newConditions.length > 0) {
    if (conditionBlocks.length > 0) {
      conditionBlocks[0].conditions.push(...(newConditions as Condition[]));
    } else {
      conditionBlocks.push({
        id: `migrated-block-${selectedRule.id}`,
        conditions: newConditions as Condition[],
      });
    }
  }

  return conditionBlocks;
}

function migrateLegacyCalendarFilter(
  selectedRule: RuleModel,
  conditionBlocks: ConditionBlock[]
) {
  const { personalCalendarFilter } = selectedRule;
  if (!personalCalendarFilter?.hasPersonalCalendarFilter) {
    return conditionBlocks;
  }

  const allSharingConditions: Partial<Condition>[] = [];

  const newConditions: Partial<Condition>[] = [];

  if (personalCalendarFilter.sharedToUserExternalToDomain) {
    allSharingConditions.push({
      id: `migrated-calendar-external-${selectedRule.id}`,
      fieldInfoType: 'calendarSharing',
      fieldName: 'sharedToUserExternalToDomain',
      operator: 'equals',
      value: 'true',
    });
  }

  if (personalCalendarFilter.sharedToUserInternalToDomain) {
    allSharingConditions.push({
      id: `migrated-calendar-internal-${selectedRule.id}`,
      fieldInfoType: 'calendarSharing',
      fieldName: 'sharedToUserInternalToDomain',
      operator: 'equals',
      value: 'true',
    });
  }

  if (personalCalendarFilter.sharedToSpecificUsers?.length) {
    personalCalendarFilter.sharedToSpecificUsers.forEach((user, index) => {
      allSharingConditions.push({
        id: `migrated-calendar-user-${selectedRule.id}-${index}`,
        fieldInfoType: 'calendarSharing',
        fieldName: 'specificUser',
        operator: 'equals',
        value: user,
        logicalOperator:
          index < personalCalendarFilter.sharedToSpecificUsers!.length - 1
            ? 'OR'
            : undefined,
      });
    });
  }

  if (personalCalendarFilter.sharedToGroups?.length) {
    personalCalendarFilter.sharedToGroups.forEach((group, index) => {
      allSharingConditions.push({
        id: `migrated-calendar-group-${selectedRule.id}-${index}`,
        fieldInfoType: 'calendarSharing',
        fieldName: 'sharedToGroups',
        operator: 'equals',
        value: group,
        logicalOperator:
          index < personalCalendarFilter.sharedToGroups!.length - 1
            ? 'OR'
            : undefined,
      });
    });
  }

  // Join all sharing conditions with OR
  if (allSharingConditions.length > 0) {
    allSharingConditions.forEach((cond, index) => {
      if (index < allSharingConditions.length - 1) {
        cond.logicalOperator = 'OR';
      }
    });
    newConditions.push(...allSharingConditions);
  }

  if (newConditions.length > 0) {
    if (conditionBlocks.length > 0) {
      conditionBlocks[0].conditions.push(...(newConditions as Condition[]));
    } else {
      conditionBlocks.push({
        id: `migrated-block-${selectedRule.id}`,
        conditions: newConditions as Condition[],
      });
    }
  }

  // Clear legacy filter to prevent it from being loaded into the form state
  delete selectedRule.personalCalendarFilter;

  return conditionBlocks;
}

export function migrateRuleToConditionBlocks(rule: RuleModel): RuleModel {
  if (!rule) return rule;

  // If condition blocks already exist, we assume the rule is already migrated
  if (rule.conditionBlocks && rule.conditionBlocks.length > 0) {
    return rule;
  }

  const migratedRule = structuredClone(rule);
  let conditionBlocks = migratedRule.conditionBlocks || [];

  conditionBlocks = migrateLegacyProfileFilter(migratedRule, conditionBlocks);
  conditionBlocks = migrateLegacyGroupFilter(migratedRule, conditionBlocks);
  conditionBlocks = migrateLegacyDriveFilter(migratedRule, conditionBlocks);
  conditionBlocks = migrateLegacyCalendarFilter(migratedRule, conditionBlocks);

  if (conditionBlocks.length > 0) {
    migratedRule.conditionBlocks = conditionBlocks;
  }

  return migratedRule;
}

export function getRulesEngineFormDefaultValues({
  pageMode,
  selectedRule,
  loggedInUser,
  allPolicies,
  userDriveFolders,
  sharedDrives,
  features,
  autoSelectPolicyId,
  emailSubject,
  emailMessage,
}: {
  pageMode: 'create' | 'edit' | 'clone';
  selectedRule?: RuleModel | null;
  loggedInUser: string;
  allPolicies: Policy[] | undefined;
  userDriveFolders?: (Doc | undefined)[];
  sharedDrives?: SharedDriveModel[];
  features: Record<string, boolean> | null;
  autoSelectPolicyId?: number;
  emailSubject?: string;
  emailMessage?: string;
}): RulesEngineFormValues {
  const autoPolicy = autoSelectPolicyId
    ? allPolicies?.find((p) => p.id === autoSelectPolicyId)
    : undefined;

  const defaultValues: RulesEngineFormValues = {
    id: 0,
    basicInfo: {
      ruleName: '',
      description: '',
      status: 'Enabled',
      creator: loggedInUser,
      createdDate: Date.now(),
      lastModifiedDate: 0,
    },
    triggerInfo: {
      occursEverytime: true,
      hasTimeDuration: false,
      triggerGroup: 'admin',
      triggerEvent: 'CREATE_USER',
      triggerThreshold: 1,
      triggerDuration: 1,
      triggerDurationType: 'days',
      triggerDomainSources: [],
      triggerGroupSources: [],
      triggerOrgSources: [],
      triggerUserSources: [],
      triggerCalendarSources: [],
      sharedDriveIds: [],
      driveRegExs: [],
      driveFolderTriggerSources: [],
      driveFileSizeFloor: 0,
      driveFileSizeCeiling: 0,
      fileMimeTypes: [],
      fileIsSharedToDomain: false,
      fileIsSharedPublic: false,
      fileSharedToSpecificUser: [],
      canFindInSearchResults: false,
      mustHaveLinkToAccess: false,
      fileSharedToUserExternalToDomain: false,
      fileSharedToUserInternalToDomain: false,
    },
    actionInfo: {
      actionTodo: [],
      payload: '',
      endpointUrl: '',
      requestMethod: 'POST',
      newPassword: '',
      timeZone: 'UTC',
      occurrenceLimit: 0,
    },
    emailInfo: {
      emailNotificationEmails: [],
      ccEmails: [],
      bccEmails: [],
      emailSubject: emailSubject ?? '',
      emailMessage: emailMessage ?? '',
    },
    chatInfo: { chatUrl: '', chatMessage: '' },
    policyInfo: {
      policyId: autoPolicy?.id ?? 0,
      policyType: autoPolicy?.policyType ?? '',
    },
    groupInfo: { groupEmail: [], groupRole: [] },
    removeGroupInfo: { groupEmail: [] },
    groupSettingsInfo: { groupTemplateId: 0 },
    allSource: false,
    targetsExcluded: [],
    triggerSources: [],
    hasProfileFieldFilter: false,
    profileFieldFilter: {
      profileFieldType: '',
      profileFieldValue: '',
      profileFieldValueExact: false,
      profileFieldRegex: '',
    },
    groupFilter: {
      hasGroupNameFilter: false,
      groupNameMode: 'name',
      groupName: '',
      groupNameExact: false,
      groupNameRegex: '',
      hasMemberCountFilter: false,
      memberCountMode: 'single',
      memberCountOperator: 'equals',
      memberCount: 0,
      memberCountRangeLessThan: 0,
      memberCountRangeGreaterThan: 0,
    },
    personalCalendarFilter: {
      hasPersonalCalendarFilter: false,
      sharedToUserExternalToDomain: false,
      sharedToUserInternalToDomain: false,
      sharedToSpecificUsers: [],
      sharedToGroups: [],
    },
  };

  if ((pageMode === 'edit' || pageMode === 'clone') && selectedRule) {
    const ruleForForm = isFeatureEnabled('rulesEnginev2UiReact', features)
      ? migrateRuleToConditionBlocks(selectedRule)
      : selectedRule;

    const triggerSources = [
      ...(ruleForForm.triggerInfo.triggerUserSources?.map((value) => ({
        type: 'User',
        value,
      })) || []),
      ...(ruleForForm.triggerInfo.triggerGroupSources?.map((value) => ({
        type: 'Group',
        value,
      })) || []),
      ...(ruleForForm.triggerInfo.triggerOrgSources?.map((value) => ({
        type: 'Orgs',
        value,
      })) || []),
      ...(ruleForForm.triggerInfo.triggerDomainSources?.map((value) => ({
        type: 'Domain',
        value,
      })) || []),
      ...(ruleForForm.triggerInfo.sharedDriveIds?.map((id) => {
        const drive = sharedDrives?.find((d) => d.id === id);

        return {
          type: 'Shared Drive',
          value: drive?.name,
          selectedSharedDriveId: id,
        };
      }) || []),
      ...(ruleForForm.triggerInfo.driveRegExs?.map((value) => ({
        type: 'RegEx',
        value,
      })) || []),
      ...(ruleForForm.triggerInfo.driveFolderTriggerSources?.map((s) => {
        const folder = userDriveFolders?.find((f) => f?.id === s.driveFolderId);
        return {
          type: 'User Drive',
          value: s.driveFolderOwner,
          driveFolderOwner: s.driveFolderOwner,
          driveFolderId: s.driveFolderId,
          driveFolderName: folder?.title,
        };
      }) || []),
    ];

    const { driveFileSizeFloor, driveFileSizeCeiling } =
      ruleForForm.triggerInfo;

    const getSizeComparison = () => {
      if ((driveFileSizeFloor ?? 0) > 0 && (driveFileSizeCeiling ?? 0) > 0) {
        return 'between';
      }
      if ((driveFileSizeFloor ?? 0) > 0) {
        return 'larger';
      }
      if ((driveFileSizeCeiling ?? 0) > 0) {
        return 'smaller';
      }
    };

    const getDisplaySizeAndType = () => {
      let floor = driveFileSizeFloor ?? 0;
      let ceiling = driveFileSizeCeiling ?? 0;
      let type: 'MB' | 'KB' | 'Byte' | undefined = undefined;

      const hasSizeFilter = floor > 0 || ceiling > 0;
      if (!hasSizeFilter) {
        return { floor, ceiling, type };
      }

      const floorInMB = floor / (1024 * 1024);
      const ceilingInMB = ceiling / (1024 * 1024);

      if (
        (floor === 0 || Number.isInteger(floorInMB)) &&
        (ceiling === 0 || Number.isInteger(ceilingInMB))
      ) {
        type = 'MB';
        floor = floorInMB;
        ceiling = ceilingInMB;
      } else {
        const floorInKB = floor / 1024;
        const ceilingInKB = ceiling / 1024;
        if (
          (floor === 0 || Number.isInteger(floorInKB)) &&
          (ceiling === 0 || Number.isInteger(ceilingInKB))
        ) {
          type = 'KB';
          floor = floorInKB;
          ceiling = ceilingInKB;
        } else {
          type = 'Byte';
        }
      }

      return { floor, ceiling, type };
    };

    const getSelectedSharingOption = () => {
      if (ruleForForm.triggerInfo.fileIsSharedPublic) {
        return 'fileIsSharedPublic';
      }
      if (ruleForForm.triggerInfo.fileIsSharedToDomain) {
        return 'fileIsSharedToDomain';
      }
      if (ruleForForm.triggerInfo.fileSharedToUserExternalToDomain) {
        return 'fileSharedToUserExternalToDomain';
      }
      if (ruleForForm.triggerInfo.fileSharedToUserInternalToDomain) {
        return 'fileSharedToUserInternalToDomain';
      }
      if ((ruleForForm.triggerInfo.fileSharedToSpecificUser?.length ?? 0) > 0) {
        return 'fileSharedToSpecificUser';
      }
      return undefined;
    };

    const getPolicyType = () => {
      if (selectedRule.policyInfo?.policyId && allPolicies) {
        return allPolicies.find(
          (p) => p.id === selectedRule.policyInfo.policyId
        )?.policyType;
      }
    };

    const baseValues = {
      ...defaultValues, // Start with a complete default structure
      ...ruleForForm, // Spread the ruleForForm
      conditionBlocks: (ruleForForm.conditionBlocks || []).map((block) => {
        const conditions = block.conditions || [];
        return {
          ...block,
          conditions: conditions.map((cond, index) => {
            const cleanCond = { ...cond } as any;
            if (
              cleanCond.logicalOperator === '' ||
              cleanCond.logicalOperator === null
            ) {
              delete cleanCond.logicalOperator;
            }

            // Ensure operator is always set to a valid default if missing or null
            const fieldInfoType = cleanCond.fieldInfoType;
            cleanCond.operator =
              cleanCond.operator ||
              (fieldInfoType === 'fileSize'
                ? 'greaterThan'
                : fieldInfoType === 'memberCount'
                ? 'greaterThanOrEqual'
                : fieldInfoType === 'fileType' ||
                  fieldInfoType === 'fileSharing'
                ? 'equals'
                : 'equals');

            if (index < conditions.length - 1) {
              return {
                ...cleanCond,
                logicalOperator: cleanCond.logicalOperator || 'AND',
              };
            }
            return cleanCond;
          }),
        };
      }),
      allSource: selectedRule.allSource,
      targetsExcluded: selectedRule.targetsExcluded,
      hasFileTypeFilter:
        !!selectedRule.triggerInfo.fileMimeTypes &&
        selectedRule.triggerInfo.fileMimeTypes.length > 0,
      hasFileSizeFilter:
        (selectedRule.triggerInfo.driveFileSizeFloor ?? 0) > 0 ||
        (selectedRule.triggerInfo.driveFileSizeCeiling ?? 0) > 0,
      hasSharingPermissionsFilter:
        selectedRule.triggerInfo.fileIsSharedPublic ||
        selectedRule.triggerInfo.fileIsSharedToDomain ||
        selectedRule.triggerInfo.fileSharedToUserExternalToDomain ||
        selectedRule.triggerInfo.fileSharedToUserInternalToDomain ||
        (selectedRule.triggerInfo.fileSharedToSpecificUser?.length ?? 0) > 0,
      hasSelectedDomainSharing:
        selectedRule.triggerInfo.fileIsSharedToDomain &&
        (selectedRule.triggerInfo.canFindInSearchResults ||
          selectedRule.triggerInfo.mustHaveLinkToAccess),
      sizeComparison: getSizeComparison(),
      sizeType: getDisplaySizeAndType().type,
      triggerInfo: {
        ...defaultValues.triggerInfo,
        ...ruleForForm.triggerInfo,
        driveFileSizeFloor: getDisplaySizeAndType().floor,
        driveFileSizeCeiling: getDisplaySizeAndType().ceiling,
      },
      selectedSharingOption: getSelectedSharingOption(),
      // Ensure nested objects are always defined by merging with defaults
      actionInfo: {
        ...defaultValues.actionInfo,
        ...ruleForForm.actionInfo,
      },
      emailInfo: {
        ...defaultValues.emailInfo,
        ...ruleForForm.emailInfo,
      },
      chatInfo: {
        ...defaultValues.chatInfo,
        ...selectedRule.chatInfo,
      },
      policyInfo: {
        ...defaultValues.policyInfo,
        ...ruleForForm.policyInfo,
        policyType: getPolicyType() || '',
        ...(autoPolicy && {
          policyId: autoPolicy.id,
          policyType: autoPolicy.policyType,
        }),
      },
      groupInfo: {
        ...defaultValues.groupInfo,
        ...ruleForForm.groupInfo,
      },
      removeGroupInfo: {
        ...defaultValues.removeGroupInfo,
        ...ruleForForm.removeGroupInfo,
      },
      groupSettingsInfo: {
        ...defaultValues.groupSettingsInfo,
        ...ruleForForm.groupSettingsInfo,
      },
      triggerSources,
      // Hydrate profile filter fields for edit mode
      profileFieldFilter: {
        ...defaultValues.profileFieldFilter,
        ...ruleForForm.profileFieldFilter,
        profileInfoType: (() => {
          const fieldType = selectedRule.profileFieldFilter?.profileFieldType;
          if (!fieldType) return '';
          if (fieldType.includes('.')) {
            return 'customAttributes';
          }
          if (employeeInfoFields.some((f) => f.value === fieldType)) {
            return 'employeeInformation';
          }
          if (profileFieldOptions.some((f) => f.value === fieldType)) {
            return 'primaryOrganization';
          }
          return '';
        })(),
        // Transform profileFieldValues from string[] to { value: string }[] for the form
        profileFieldValues: selectedRule.profileFieldFilter?.profileFieldValues
          ?.length
          ? selectedRule.profileFieldFilter.profileFieldValues.map(
              (value: any) => ({
                value: typeof value === 'string' ? value : value?.value ?? '',
              })
            )
          : selectedRule.profileFieldFilter?.profileFieldValue
          ? [{ value: selectedRule.profileFieldFilter.profileFieldValue }]
          : [{ value: '' }],
      },
      groupFilter: {
        ...defaultValues.groupFilter,
        ...ruleForForm.groupFilter,
        groupNameMode:
          ruleForForm.groupFilter?.groupNameMode ??
          defaultValues.groupFilter?.groupNameMode,
        memberCountMode:
          ruleForForm.groupFilter?.memberCountMode ??
          defaultValues.groupFilter?.memberCountMode,
        memberCountOperator:
          ruleForForm.groupFilter?.memberCountOperator ??
          defaultValues.groupFilter?.memberCountOperator,
      },
      personalCalendarFilter: {
        ...defaultValues.personalCalendarFilter,
        ...ruleForForm.personalCalendarFilter,
      },
    } as RulesEngineFormValues;

    return baseValues as RulesEngineFormValues;
  }

  return defaultValues;
}

export const cloneRule = (rule: RuleModel): RuleModel => {
  const clonedRule = structuredClone(rule);
  clonedRule.id = 0;
  clonedRule.basicInfo.ruleName = `${rule.basicInfo.ruleName} - COPY`;
  clonedRule.basicInfo.status = 'Draft';
  clonedRule.basicInfo.createdDate = 0;
  clonedRule.basicInfo.lastModifiedDate = 0;

  return clonedRule;
};

// Helper function to prepare rule data for submission
export const prepareRuleData = (data: RulesEngineForm): RuleModel => {
  const sources = {
    triggerDomainSources: data
      .triggerSources!.filter((x) => x.type === 'Domain')
      .map((x) => x.value),
    triggerGroupSources: data
      .triggerSources!.filter((x) => x.type === 'Group')
      .map((x) => x.value),
    triggerOrgSources: data
      .triggerSources!.filter((x) => x.type === 'Orgs')
      .map((x) => x.value),
    triggerUserSources:
      data.triggerSources
        ?.filter((x) => x.type === 'User')
        .map((x) => x.value) ?? [],
    triggerCalendarSources: data.triggerInfo.triggerCalendarSources,
    sharedDriveIds: data
      .triggerSources!.filter((x) => x.type === 'Shared Drive')
      .map((x) => x.selectedSharedDriveId || ''),
    driveRegExs: data
      .triggerSources!.filter((x) => x.type === 'RegEx')
      .map((x) => x.value),
    driveFolderTriggerSources: data
      .triggerSources!.filter((x) => x.type === 'User Drive')
      .map((x) => {
        return {
          driveFolderOwner: x.driveFolderOwner!,
          driveFolderId: x.driveFolderId!,
        };
      }),
  };

  const clonedData = structuredClone(data);

  if (clonedData.conditionBlocks) {
    clonedData.conditionBlocks = clonedData.conditionBlocks.map((block) => {
      const conditions = block.conditions || [];
      return {
        ...block,
        conditions: conditions.map((cond, index) => {
          // Ensure operator is always a valid value
          let operator = cond.operator;
          if (!operator) {
            const fieldInfoType = cond.fieldInfoType;
            operator =
              fieldInfoType === 'fileSize'
                ? 'larger'
                : fieldInfoType === 'memberCount'
                ? 'greaterThanOrEqual'
                : fieldInfoType === 'fileType' ||
                  fieldInfoType === 'fileSharing'
                ? 'equals'
                : 'contains';
          }

          if (index < conditions.length - 1) {
            return {
              ...cond,
              operator,
              logicalOperator: cond.logicalOperator || 'AND',
            };
          }
          const cleanCond = { ...cond, operator };
          delete cleanCond.logicalOperator;
          return cleanCond;
        }),
      };
    });
  }

  delete clonedData.triggerSources;
  delete clonedData.hasFileTypeFilter;
  if (!clonedData.hasProfileFieldFilter) {
    delete clonedData.profileFieldFilter;
  } else if (clonedData.profileFieldFilter) {
    // strip UI-only fields
    delete clonedData.profileFieldFilter.profileInfoType;
    delete clonedData.profileFieldFilter.valueInputMode;
    const stringValues = (
      clonedData.profileFieldFilter.profileFieldValues ?? []
    )
      .map((item) => item.value)
      .filter((v) => v && v.trim() !== '');
    delete clonedData.profileFieldFilter.profileFieldValues;

    if (stringValues.length > 1) {
      (clonedData.profileFieldFilter as any).profileFieldValues = stringValues;
      clonedData.profileFieldFilter.profileFieldValue = '';
    } else if (stringValues.length === 1) {
      // Single value: use single field, clear array
      clonedData.profileFieldFilter.profileFieldValue = stringValues[0];

      (clonedData.profileFieldFilter as any).profileFieldValues = [];
    } else {
      // No values: clear both
      clonedData.profileFieldFilter.profileFieldValue = '';
      (clonedData.profileFieldFilter as any).profileFieldValues = [];
    }
  }
  delete clonedData.hasProfileFieldFilter;

  if (clonedData.hasFileSizeFilter) {
    if (clonedData.sizeComparison === 'larger') {
      clonedData.triggerInfo.driveFileSizeCeiling = 0;
    } else if (clonedData.sizeComparison === 'smaller') {
      clonedData.triggerInfo.driveFileSizeFloor = 0;
    }
  } else {
    clonedData.triggerInfo.driveFileSizeFloor = 0;
    clonedData.triggerInfo.driveFileSizeCeiling = 0;
  }

  // convert the file size to bytes
  if (clonedData.sizeType === 'KB') {
    clonedData.triggerInfo.driveFileSizeFloor =
      clonedData.triggerInfo.driveFileSizeFloor * 1024;
    clonedData.triggerInfo.driveFileSizeCeiling =
      clonedData.triggerInfo.driveFileSizeCeiling * 1024;
  } else if (clonedData.sizeType === 'MB') {
    clonedData.triggerInfo.driveFileSizeFloor =
      clonedData.triggerInfo.driveFileSizeFloor * 1024 * 1024;
    clonedData.triggerInfo.driveFileSizeCeiling =
      clonedData.triggerInfo.driveFileSizeCeiling * 1024 * 1024;
  }

  delete clonedData.hasFileSizeFilter;
  delete clonedData.sizeComparison;
  delete clonedData.sizeType;

  if (clonedData.hasSharingPermissionsFilter) {
    clonedData.triggerInfo.fileIsSharedToDomain =
      clonedData.selectedSharingOption === 'fileIsSharedToDomain';
    clonedData.triggerInfo.fileIsSharedPublic =
      clonedData.selectedSharingOption === 'fileIsSharedPublic';
    clonedData.triggerInfo.fileSharedToUserExternalToDomain =
      clonedData.selectedSharingOption === 'fileSharedToUserExternalToDomain';
    clonedData.triggerInfo.fileSharedToUserInternalToDomain =
      clonedData.selectedSharingOption === 'fileSharedToUserInternalToDomain';
  }

  delete clonedData.hasSharingPermissionsFilter;
  delete clonedData.selectedSharingOption;
  delete clonedData.hasSelectedDomainSharing;

  // Group filter only applies to group-membership events; strip it otherwise.
  const groupEvents = [
    'ADD_GROUP_MEMBER',
    'REMOVE_GROUP_MEMBER',
    'UPDATE_GROUP_MEMBER',
  ];
  const isGroupEvent = groupEvents.includes(
    clonedData.triggerInfo?.triggerEvent ?? ''
  );
  const groupFilter = clonedData.groupFilter;
  if (
    !isGroupEvent ||
    !groupFilter ||
    (!groupFilter.hasGroupNameFilter && !groupFilter.hasMemberCountFilter)
  ) {
    delete clonedData.groupFilter;
  } else {
    if (!groupFilter.hasGroupNameFilter) {
      groupFilter.groupName = '';
      groupFilter.groupNameRegex = '';
      groupFilter.groupNameExact = false;
    } else if ((groupFilter.groupNameMode ?? 'name') === 'name') {
      groupFilter.groupNameRegex = '';
    } else {
      groupFilter.groupName = '';
      groupFilter.groupNameExact = false;
    }
    if (!groupFilter.hasMemberCountFilter) {
      groupFilter.memberCount = 0;
      groupFilter.memberCountRangeLessThan = 0;
      groupFilter.memberCountRangeGreaterThan = 0;
    } else {
      // Range mode is only valid for the "Any Membership changes" event.
      if (
        groupFilter.memberCountMode === 'range' &&
        clonedData.triggerInfo?.triggerEvent !== 'UPDATE_GROUP_MEMBER'
      ) {
        groupFilter.memberCountMode = 'single';
      }
      if (groupFilter.memberCountMode === 'range') {
        groupFilter.memberCount = 0;
      } else {
        groupFilter.memberCountRangeLessThan = 0;
        groupFilter.memberCountRangeGreaterThan = 0;
      }
    }
  }

  return {
    ...clonedData,
    allSource: data.allSource, // Explicitly carry over the allSource value
    triggerInfo: {
      ...clonedData.triggerInfo!,
      ...sources,
    },
    emailInfo: {
      ...clonedData.emailInfo,
      emailSubject: clonedData.emailInfo?.emailSubject
        ?.replace(/(<([^>]+)>)/gi, '') // Remove HTML tags
        .replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
        .trim(), // Remove leading/trailing whitespace
      emailMessage: clonedData.emailInfo?.emailMessage
        ?.replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
        .trim(), // Remove leading/trailing whitespace
      emailNotificationEmails:
        clonedData.emailInfo?.emailNotificationEmails?.map((email) => email) ??
        [],
      ccEmails: clonedData.emailInfo?.ccEmails ?? [],
      bccEmails: clonedData.emailInfo?.bccEmails ?? [],
    },
    chatInfo: {
      ...clonedData.chatInfo,
      chatMessage: clonedData.chatInfo?.chatMessage
        ?.replace(/(<([^>]+)>)/gi, '') // Remove HTML tags
        .replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
        .trim(), // Remove leading/trailing whitespace
      chatUrl: clonedData.chatInfo?.chatUrl ?? '',
    },
  } as RuleModel;
};
